import React, { useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useTemplate } from '@/context/TemplateContext';
import { useDisplaySettings } from '@/hooks/useDisplaySettings';
import { Star, UserPlus, CheckCircle, MapPin } from 'lucide-react';

// Sample data for interactive notifications
const firstNames = ['Sarah', 'Mike', 'Jennifer', 'David', 'Emily', 'Robert', 'Lisa', 'James', 'Amanda', 'Chris'];
const lastInitials = ['M.', 'K.', 'L.', 'R.', 'T.', 'W.', 'B.', 'S.', 'P.', 'D.'];

const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const getRandomMinutes = () => Math.floor(Math.random() * 45) + 5;

export const InteractiveToasts: React.FC = () => {
  const { toast } = useToast();
  const { template } = useTemplate();
  const { settings } = useDisplaySettings();
  const showNewCustomerToast = useCallback(() => {
    const name = `${getRandomItem(firstNames)} ${getRandomItem(lastInitials)}`;
    const area = getRandomItem(template.serviceAreas);
    
    toast({
      title: "🎉 New Customer!",
      description: (
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-green-500" />
          <span>{name} from {area.name} just requested a quote!</span>
        </div>
      ),
      duration: 5000,
    });
  }, [toast, template.serviceAreas]);

  const showReviewToast = useCallback(() => {
    const name = `${getRandomItem(firstNames)} ${getRandomItem(lastInitials)}`;
    const service = getRandomItem(template.services);
    const rating = 5;
    
    toast({
      title: "⭐ New 5-Star Review!",
      description: (
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            {Array.from({ length: rating }).map((_, i) => (
              <Star key={i} className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            ))}
          </div>
          <p className="text-sm">{name} left a review for {service.title}</p>
        </div>
      ),
      duration: 5000,
    });
  }, [toast, template.services]);

  const showJobCompletedToast = useCallback(() => {
    const service = getRandomItem(template.services);
    const area = getRandomItem(template.serviceAreas);
    const minutes = getRandomMinutes();
    
    toast({
      title: "✅ Job Completed!",
      description: (
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span>{service.title} completed in {area.name} - {minutes} min ago</span>
        </div>
      ),
      duration: 5000,
    });
  }, [toast, template.services, template.serviceAreas]);

  const showServiceAreaToast = useCallback(() => {
    const area = getRandomItem(template.serviceAreas);
    
    toast({
      title: "📍 Now Serving",
      description: (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          <span>We're actively serving customers in {area.name} today!</span>
        </div>
      ),
      duration: 5000,
    });
  }, [toast, template.serviceAreas]);

  useEffect(() => {
    // Don't run if toasts are disabled
    if (!settings.show_interactive_toasts) return;

    const toastFunctions = [
      showNewCustomerToast,
      showReviewToast,
      showJobCompletedToast,
      showServiceAreaToast,
    ];

    // Show first toast after 8 seconds
    const initialTimeout = setTimeout(() => {
      getRandomItem(toastFunctions)();
    }, 8000);

    // Then show toasts every 25-45 seconds
    const interval = setInterval(() => {
      getRandomItem(toastFunctions)();
    }, 25000 + Math.random() * 20000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [settings.show_interactive_toasts, showNewCustomerToast, showReviewToast, showJobCompletedToast, showServiceAreaToast]);

  return null; // This component only triggers toasts, doesn't render anything
};
